import { useState } from 'react';
import Modal from '@/components/base/Modal';
import { classSections, documentTypes, classTeacherMap, type StudentRecord } from '@/mocks/students';

type EnrollmentStep = 1 | 2 | 3;

interface EnrollmentModalProps {
  open: boolean;
  onClose: () => void;
  onSaveDraft: (data: Partial<StudentRecord>) => void;
  onSubmit: (data: Partial<StudentRecord>) => void;
  initialData?: Partial<StudentRecord>;
}

const steps = [
  { step: 1 as EnrollmentStep, label: 'Demographics', icon: 'ri-user-line' },
  { step: 2 as EnrollmentStep, label: 'Guardian Details', icon: 'ri-parent-line' },
  { step: 3 as EnrollmentStep, label: 'Documents', icon: 'ri-file-list-3-line' },
];

export default function EnrollmentModal({
  open,
  onClose,
  onSaveDraft,
  onSubmit,
  initialData,
}: EnrollmentModalProps) {
  const [currentStep, setCurrentStep] = useState<EnrollmentStep>(1);
  const [formData, setFormData] = useState<Partial<StudentRecord>>(initialData || {});
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, string>>({});

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDocUpload = (docName: string) => {
    const fakeFileName = `${docName.replace(/\s+/g, '_')}.pdf`;
    setUploadedDocs((prev) => ({ ...prev, [docName]: fakeFileName }));
  };

  const handleRemoveDoc = (docName: string) => {
    setUploadedDocs((prev) => {
      const next = { ...prev };
      delete next[docName];
      return next;
    });
  };

  const handleSaveDraft = () => {
    onSaveDraft({ ...formData, status: 'draft' });
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    onSubmit({ ...formData, status: 'enrolled' });
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setCurrentStep(1);
    setFormData({});
    setUploadedDocs({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const canProceed = (): boolean => {
    if (currentStep === 1) {
      return !!(formData.name && formData.dob && formData.className && formData.aadhaarNumber);
    }
    if (currentStep === 2) {
      return !!(formData.fatherName && formData.motherName && formData.contactNumberF && formData.district);
    }
    return true;
  };

  const stepLabels: Record<EnrollmentStep, string> = {
    1: 'Student Demographics',
    2: 'Guardian / Parent Information',
    3: 'Document Upload',
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={stepLabels[currentStep]}
      subtitle={`Step ${currentStep} of 3`}
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div>
            <button
              onClick={handleSaveDraft}
              className="inline-flex items-center gap-2 h-10 px-4 bg-foreground-100 text-foreground-700 text-sm font-label font-medium rounded-lg hover:bg-foreground-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-save-line" />
              Save Draft
            </button>
          </div>
          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep((s) => (s - 1) as EnrollmentStep)}
                className="inline-flex items-center gap-2 h-10 px-4 bg-background-100 text-foreground-600 text-sm font-label font-medium rounded-lg hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                Previous
              </button>
            )}
            {currentStep < 3 ? (
              <button
                onClick={() => setCurrentStep((s) => (s + 1) as EnrollmentStep)}
                disabled={!canProceed()}
                className={`inline-flex items-center gap-2 h-10 px-5 text-sm font-label font-medium rounded-lg transition-colors whitespace-nowrap ${
                  canProceed()
                    ? 'bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer'
                    : 'bg-background-200 text-foreground-400 cursor-not-allowed'
                }`}
              >
                Next
                <i className="ri-arrow-right-line" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="inline-flex items-center gap-2 h-10 px-5 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-line" />
                Submit (Enroll)
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* ── Stepper ─────────────────────────────────────────── */}
      <div className="flex items-center justify-center gap-0 mb-8">
        {steps.map((s, idx) => {
          const isActive = s.step === currentStep;
          const isCompleted = s.step < currentStep;
          return (
            <div key={s.step} className="flex items-center">
              {/* Step circle */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-label font-bold transition-all duration-200 ${
                    isCompleted
                      ? 'bg-primary-500 text-background-50'
                      : isActive
                        ? 'bg-primary-500 text-background-50 ring-4 ring-primary-100'
                        : 'bg-background-100 text-foreground-400'
                  }`}
                >
                  {isCompleted ? <i className="ri-check-line" /> : s.step}
                </div>
                <span
                  className={`mt-2 text-xs font-label font-medium whitespace-nowrap ${
                    isActive ? 'text-primary-600' : isCompleted ? 'text-foreground-600' : 'text-foreground-400'
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {/* Connector line */}
              {idx < steps.length - 1 && (
                <div className={`w-16 sm:w-24 h-0.5 mx-2 mt-[-18px] rounded-full transition-colors ${
                  s.step < currentStep ? 'bg-primary-500' : 'bg-background-200'
                }`} />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Step 1: Demographics ────────────────────────────── */}
      {currentStep === 1 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Full Name <span className="text-semantic-error">*</span>
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Enter student's full name"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Date of Birth <span className="text-semantic-error">*</span>
              </label>
              <input
                type="date"
                value={formData.dob || ''}
                onChange={(e) => updateField('dob', e.target.value)}
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label className="text-sm font-medium text-foreground-700 font-label">Address</label>
              <input
                type="text"
                value={formData.address || ''}
                onChange={(e) => updateField('address', e.target.value)}
                placeholder="Full residential address"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Aadhaar Number <span className="text-semantic-error">*</span>
              </label>
              <input
                type="text"
                value={formData.aadhaarNumber || ''}
                onChange={(e) => updateField('aadhaarNumber', e.target.value)}
                placeholder="XXXX-XXXX-XXXX"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">UDID Number</label>
              <input
                type="text"
                value={formData.udidNumber || ''}
                onChange={(e) => updateField('udidNumber', e.target.value)}
                placeholder="UDID-XX-XXXX-XXXXXX"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Class / Section <span className="text-semantic-error">*</span>
              </label>
              <select
                value={formData.className || ''}
                onChange={(e) => updateField('className', e.target.value)}
                className="xl-input cursor-pointer"
              >
                <option value="">Select class section</option>
                {classSections.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Assigned Teacher
              </label>
              <div className="xl-input bg-background-100 text-foreground-500 flex items-center gap-2 select-none">
                <i className="ri-user-star-line text-base text-foreground-400" />
                <span>
                  {formData.className ? (classTeacherMap[formData.className] || 'No teacher assigned') : '— Select a class first —'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Guardian Details ────────────────────────── */}
      {currentStep === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Father Name <span className="text-semantic-error">*</span>
              </label>
              <input
                type="text"
                value={formData.fatherName || ''}
                onChange={(e) => updateField('fatherName', e.target.value)}
                placeholder="Father's full name"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Mother Name <span className="text-semantic-error">*</span>
              </label>
              <input
                type="text"
                value={formData.motherName || ''}
                onChange={(e) => updateField('motherName', e.target.value)}
                placeholder="Mother's full name"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                Contact Number (Father) <span className="text-semantic-error">*</span>
              </label>
              <input
                type="text"
                value={formData.contactNumberF || ''}
                onChange={(e) => updateField('contactNumberF', e.target.value)}
                placeholder="+91 XXXXX XXXXX"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Contact Number (Mother)</label>
              <input
                type="text"
                value={formData.contactNumberM || ''}
                onChange={(e) => updateField('contactNumberM', e.target.value)}
                placeholder="+91 XXXXX XXXXX"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Email Address</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="parent@email.com"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">
                District <span className="text-semantic-error">*</span>
              </label>
              <input
                type="text"
                value={formData.district || ''}
                onChange={(e) => updateField('district', e.target.value)}
                placeholder="District name"
                className="xl-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground-700 font-label">Town / City</label>
              <input
                type="text"
                value={formData.town || ''}
                onChange={(e) => updateField('town', e.target.value)}
                placeholder="Town or city name"
                className="xl-input"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Step 3: Documents ───────────────────────────────── */}
      {currentStep === 3 && (
        <div className="space-y-1">
          <p className="text-sm text-foreground-500 font-label mb-4">
            Upload the required documents. Accepted formats: PDF, JPG, PNG (max 5 MB each).
          </p>
          <div className="space-y-2">
            {documentTypes.map((doc) => {
              const isUploaded = !!uploadedDocs[doc.name];
              return (
                <div
                  key={doc.name}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    isUploaded
                      ? 'border-semantic-success/40 bg-semantic-success-bg/60'
                      : 'border-background-200 bg-background-50 hover:bg-background-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${
                      isUploaded ? 'bg-semantic-success/15' : 'bg-background-100'
                    }`}>
                      <i className={`text-lg ${
                        isUploaded
                          ? 'ri-checkbox-circle-fill text-semantic-success'
                          : 'ri-file-upload-line text-foreground-400'
                      }`} />
                    </div>
                    <div>
                      <p className="text-sm font-label font-medium text-foreground-800">
                        {doc.name}
                        {doc.required && <span className="text-semantic-error ml-1">*</span>}
                      </p>
                      {isUploaded && (
                        <p className="text-xs text-foreground-500 font-mono">{uploadedDocs[doc.name]}</p>
                      )}
                    </div>
                  </div>
                  {isUploaded ? (
                    <button
                      onClick={() => handleRemoveDoc(doc.name)}
                      className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-label font-medium text-semantic-error bg-semantic-error-bg rounded-md hover:bg-semantic-error-bg/80 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line" />
                      Remove
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDocUpload(doc.name)}
                      className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-label font-medium text-accent-700 bg-accent-100 rounded-md hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-upload-cloud-2-line" />
                      Upload
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
}